import { Preferences } from "@capacitor/preferences";
import debounce from "lodash/debounce";
import { AtomEffect } from "recoil";
import { Sentry } from "../utils/sentry";

const DEBOUNCE_SAVE_MS = 300;

export const preferencesEffect =
  <T>(
    options: {
      serialize?: (value: T) => string;
      deserialize?: (json: string) => T;
      debounceMs?: number;
    } = {},
  ): AtomEffect<T> =>
  ({ setSelf, onSet, node }) => {
    const { serialize, deserialize, debounceMs } = options;
    const effectiveSerialize = serialize ?? JSON.stringify;
    const effectiveDeserialize = deserialize ?? JSON.parse;
    const effectiveDebounceMs = debounceMs ?? DEBOUNCE_SAVE_MS;

    const key = node.key;

    const loadPersisted = async () => {
      const { value: savedValue } = await Preferences.get({ key });
      if (!savedValue) {
        return;
      }

      try {
        const deserializedValue = effectiveDeserialize(savedValue) as T;
        setSelf(deserializedValue);
      } catch (e: unknown) {
        Sentry.captureException(e);
      }
    };

    void loadPersisted();

    // Debounce save calls to prevent race conditions where rapid updates
    // to async storage could otherwise lead to an older state being persisted last.
    const debouncedSave = debounce(async (valueToSave: T) => {
      try {
        await Preferences.set({
          key,
          value: effectiveSerialize(valueToSave),
        });
      } catch (e: unknown) {
        Sentry.captureException(e);
      }
    }, effectiveDebounceMs);

    const reset = async () => {
      debouncedSave.cancel();
      try {
        await Preferences.remove({ key });
      } catch (e: unknown) {
        Sentry.captureException(e);
      }
    };

    onSet((newValue, _, isReset) => {
      if (isReset) {
        void reset();
      } else {
        void debouncedSave(newValue);
      }
    });
  };
